// Angular
import { Injectable, Injector } from '@angular/core';
import {
	HttpErrorResponse,
	HttpEvent,
	HttpHandler,
	HttpInterceptor,
	HttpRequest,
} from '@angular/common/http';
// RxJS
import { Observable, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { AuthenticationService } from '../../../core/services/auth.service';

const RETRY_AFTER_REFRESH = 'X-Internal-Refresh-Retry';

/**
 * Añade Bearer; ante 401 intenta POST /auth/refresh y reintenta la petición una vez.
 */
@Injectable()
export class InterceptService implements HttpInterceptor {
	constructor(public auth: AuthenticationService, private injector: Injector) {}

	intercept(
		request: HttpRequest<any>,
		next: HttpHandler
	): Observable<HttpEvent<any>> {
		const auth = this.injector.get(AuthenticationService);
		let req = request;
		const token = auth.getToken();
		if (token) {
			req = req.clone({
				setHeaders: {
					Authorization: `Bearer ${token}`,
				},
			});
		}
		if (req.body instanceof FormData) {
			// no forzar Content-Type
		} else {
			if (!req.headers.has('Content-Type')) {
				req = req.clone({
					setHeaders: {
						'Content-Type': `application/json`,
					},
				});
			}
		}

		return next.handle(req).pipe(
			catchError((error: HttpErrorResponse) => {
				if (error.status !== 401) {
					return throwError(() => error);
				}
				if (req.headers.get(RETRY_AFTER_REFRESH) === '1') {
					return throwError(() => error);
				}
				if (this.shouldBypass401Refresh(req)) {
					return throwError(() => error);
				}
				if (!auth.getRefreshToken()) {
					return throwError(() => error);
				}
				return auth.refreshAuthTokens().pipe(
					switchMap(() => {
						const newTok = auth.getToken();
						const retryReq = req.clone({
							setHeaders: {
								Authorization: `Bearer ${newTok}`,
								[RETRY_AFTER_REFRESH]: '1',
							},
						});
						return next.handle(retryReq);
					}),
					catchError(() => {
						auth.logout();
						return throwError(() => error);
					})
				);
			})
		);
	}

	/** Login, refresh y autologin: no encadenar refresh ante 401 */
	private shouldBypass401Refresh(req: HttpRequest<any>): boolean {
		const url = req.url.split('?')[0];
		if (url.includes('/auth/refresh')) {
			return true;
		}
		if (url.includes('/autologin')) {
			return true;
		}
		if (req.method === 'POST' && /\/auth\/?$/.test(url)) {
			return true;
		}
		return false;
	}
}
